import {
    ApplicationInsights,
    IAppInsights,
    IConfig,
    IConfiguration,
} from '@microsoft/applicationinsights-web';
import { createContext, ReactNode, useCallback, useEffect, useMemo } from 'react';

import useMsalUser from '../../hooks/useMsalUser';

interface Props {
    config: IConfiguration & IConfig;
    debug?: boolean;
    children?: ReactNode;
}

interface AppInsightsContext {
    appInsights: IAppInsights | undefined | null;
    logPageView: (pageName?: string, additionalProperties?: Record<string, string>) => void;
    logTrace: (message: string, properties?: Record<string, string>) => void;
    logEvent: (eventName: string, properties?: Record<string, string>) => void;
    logException: (error: Error, properties?: Record<string, string>) => void;
}

const DefaultState: AppInsightsContext = {
    appInsights: null,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    logPageView: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    logTrace: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    logEvent: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    logException: () => {},
};

const AppInsightsContext = createContext<AppInsightsContext>(DefaultState);

const generalAIConfig = {
    enableCorsCorrelation: false,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    enableAjaxErrorStatusText: true,
    enableAjaxPerfTracking: true,
    enableAutoRouteTracking: true,
    maxBatchInterval: 5000,
};

function AppInsightsProvider({ children, config, debug = false }: Props) {
    const { getUser } = useMsalUser();

    const debugLog = useCallback(
        (message: string) => {
            if (debug) {
                console.warn(message);
            }
        },
        [debug]
    );

    const appInsights = useMemo(() => {
        if (config && config.connectionString) {
            try {
                const appInsights = new ApplicationInsights({
                    config: {
                        ...generalAIConfig,
                        ...config,
                    },
                });

                appInsights.loadAppInsights();

                return appInsights;
            } catch (e) {
                console.error(e);
            }
        } else {
            debugLog('No connection string was provided for App Insights.');
        }
    }, [config, debugLog]);

    useEffect(() => {
        if (appInsights?.context) {
            const user = getUser();

            if (user) {
                appInsights.setAuthenticatedUserContext(user.consumerResourceId, null, true);
            } else {
                appInsights.clearAuthenticatedUserContext();
            }
        }
    }, [appInsights, getUser]);

    const logPageView = useCallback(
        (pageName?: string, additionalProperties?: Record<string, string>) => {
            if (appInsights) {
                appInsights.trackPageView({
                    name: pageName ?? undefined,
                    uri: window.location.pathname,
                    refUri: window.location.href,
                    properties: additionalProperties,
                });
            } else {
                debugLog('Application Insights failed to initialize.');
            }
        },
        [appInsights, debugLog]
    );

    const logTrace = useCallback(
        (message: string, properties: Record<string, string>) => {
            if (appInsights) {
                appInsights.trackTrace(
                    {
                        message,
                    },
                    properties
                );
            } else {
                debugLog('Application Insights failed to initialize.');
            }
        },
        [appInsights, debugLog]
    );

    const logEvent = useCallback(
        (eventName: string, properties: Record<string, string>) => {
            if (appInsights) {
                appInsights.trackEvent(
                    {
                        name: eventName,
                    },
                    properties
                );
            } else {
                debugLog('Application Insights failed to initialize.');
            }
        },
        [appInsights, debugLog]
    );

    const logException = useCallback(
        (error: Error, properties: Record<string, string>) => {
            if (appInsights) {
                appInsights.trackException(
                    {
                        exception: error,
                    },
                    properties
                );
            } else {
                debugLog('Application Insights failed to initialize.');
            }
        },
        [appInsights, debugLog]
    );

    return (
        <AppInsightsContext.Provider
            value={{
                appInsights,
                logPageView,
                logTrace,
                logEvent,
                logException,
            }}>
            {children}
        </AppInsightsContext.Provider>
    );
}

export { AppInsightsContext, AppInsightsProvider };
